import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { fmtDateTime } from "../lib/format";
import {
  Trash2, Download, FileText, ShieldCheck, Plus, BadgeCheck, Phone, Mail, MapPin, Building2,
} from "lucide-react";

const TYPE_BADGE = { pdf: "bg-red-100 text-red-700", docx: "bg-blue-100 text-blue-700", doc: "bg-blue-100 text-blue-700" };

const Field = ({ label, value, mono }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400">{label}</div>
    <div className={`text-sm font-semibold text-navy ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
  </div>
);

export default function AdminBrokerForms() {
  const [profile, setProfile] = useState(null);
  const [forms, setForms] = useState([]);
  const [form, setForm] = useState({ title: "", category: "Other", description: "", file_url: "", file_type: "pdf" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [denied, setDenied] = useState(false);

  const load = async () => {
    try {
      const [p, f] = await Promise.all([
        api.get("/admin/broker-profile"),
        api.get("/admin/broker-forms"),
      ]);
      setProfile(p.data); setForms(f.data);
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/admin/broker-forms", { ...form });
      setForm({ title: "", category: "Other", description: "", file_url: "", file_type: "pdf" });
      await load();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Failed to add form");
    }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this form from the library?")) return;
    await api.delete(`/admin/broker-forms/${id}`);
    await load();
  };

  if (denied) return (
    <div className="card-base p-10 text-center max-w-lg mx-auto" data-testid="broker-forms-denied">
      <ShieldCheck size={32} className="text-slate-300 mx-auto mb-3" />
      <div className="font-display text-xl font-bold text-navy">Restricted</div>
      <p className="text-slate-500 mt-1 text-sm">Broker Forms are available to Super Admins only.</p>
    </div>
  );
  if (!profile) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-8 max-w-5xl">
      <div>
        <div className="text-[11px] tracking-[0.2em] uppercase text-gold font-bold mb-2">Super Admin</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter text-navy">Broker Forms & Authority</h1>
        <p className="text-slate-500 mt-2">Company operating authority and the official paperwork library carriers and agents are sent.</p>
      </div>

      {/* Company / Authority profile */}
      <div className="card-base overflow-hidden" data-testid="broker-profile-card">
        <div className="bg-navy px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <ShieldCheck className="text-gold" size={22} />
            <div>
              <div className="text-white font-display text-lg font-bold leading-tight">{profile.legal_name}</div>
              <div className="text-[11px] tracking-[0.2em] uppercase text-gold font-bold">{profile.entity_type} · Property Brokerage</div>
              {profile.parent_company && <div className="text-[11px] text-slate-300 mt-0.5">Owned by {profile.parent_company}</div>}
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 bg-emerald-500 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full" data-testid="broker-authority-badge">
            <BadgeCheck size={14} /> Broker Authority {profile.broker_authority}
          </span>
        </div>
        <div className="p-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <Field label="USDOT Number" value={`${profile.usdot_number} · ${profile.usdot_status}`} mono />
          <Field label="MC Number" value={profile.mc_number} mono />
          <Field label="DUNS Number" value={profile.duns_number} mono />
          <Field label="Authority Status" value={profile.authority_status} />
          <Field label="MCS-150 Date" value={profile.mcs150_date} mono />
          <Field label="CAGE Code" value={profile.cage_code} mono />
          <Field label="Unique Entity ID" value={profile.unique_id} mono />
          <Field label="Out of Service" value={profile.out_of_service_date} />
        </div>
        <div className="px-6 pb-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 border-t border-slate-100 pt-5">
          <div className="flex items-start gap-2"><MapPin size={16} className="text-gold mt-0.5 flex-shrink-0" /><div><div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400">Address</div><div className="text-sm font-semibold text-navy">{profile.physical_address}</div></div></div>
          <div className="flex items-start gap-2"><Phone size={16} className="text-gold mt-0.5 flex-shrink-0" /><div><div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400">Phone</div><div className="text-sm font-semibold text-navy font-mono">{profile.phone}</div></div></div>
          <div className="flex items-start gap-2"><Mail size={16} className="text-gold mt-0.5 flex-shrink-0" /><div><div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400">Email</div><div className="text-sm font-semibold text-navy">{profile.email}</div></div></div>
          <div className="flex items-start gap-2"><Mail size={16} className="text-gold mt-0.5 flex-shrink-0" /><div><div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400">Billing / Paperwork</div><div className="text-sm font-semibold text-navy">{profile.billing_email}</div></div></div>
          <div className="flex items-start gap-2"><Mail size={16} className="text-gold mt-0.5 flex-shrink-0" /><div><div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400">Support</div><div className="text-sm font-semibold text-navy">{profile.support_email}</div></div></div>
          <div className="flex items-start gap-2"><Building2 size={16} className="text-gold mt-0.5 flex-shrink-0" /><div><div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400">NAICS Codes</div><div className="text-xs font-semibold text-navy font-mono">{profile.naics_codes.join(", ")}</div></div></div>
        </div>
      </div>

      {/* Forms library */}
      <div>
        <div className="font-display text-xl font-bold text-navy mb-4">Forms Library</div>
        <div className="grid md:grid-cols-2 gap-4" data-testid="broker-forms-list">
          {forms.length === 0 && <div className="text-slate-400 text-sm">No forms yet.</div>}
          {forms.map((f) => (
            <div key={f.form_id} className="card-base p-5 flex flex-col" data-testid={`form-${f.form_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-md bg-gold/15 flex items-center justify-center flex-shrink-0">
                    <FileText size={18} className="text-gold" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-navy leading-tight">{f.title}</div>
                    <span className="inline-block mt-1 text-[10px] uppercase tracking-wider font-bold text-slate-500">{f.category}</span>
                  </div>
                </div>
                <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${TYPE_BADGE[f.file_type] || "bg-slate-100 text-slate-600"}`}>{f.file_type}</span>
              </div>
              <p className="text-sm text-slate-500 mt-3 flex-1">{f.description}</p>
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                <a href={f.file_url} target="_blank" rel="noreferrer" className="btn-secondary text-xs" data-testid={`download-${f.form_id}`}>
                  <Download size={14} /> Download
                </a>
                <button onClick={() => remove(f.form_id)} className="text-red-600 hover:text-red-800 p-1.5" data-testid={`delete-form-${f.form_id}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add form */}
      <form onSubmit={submit} className="card-base p-6 space-y-4" data-testid="add-form">
        <div className="font-display text-lg font-bold text-navy flex items-center gap-2"><Plus size={18} className="text-gold" /> Add a form to the library</div>
        {err && <div className="text-sm text-red-600 font-semibold" data-testid="add-form-error">{err}</div>}
        <div className="grid md:grid-cols-2 gap-3">
          <input className="input-base" placeholder="Form title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="form-title" />
          <input className="input-base" placeholder="Category (e.g. Compliance)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="form-category" />
        </div>
        <input className="input-base" placeholder="File URL (https://…)" value={form.file_url} onChange={(e) => setForm({ ...form, file_url: e.target.value })} required data-testid="form-url" />
        <textarea className="input-base" rows={2} placeholder="Short description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="form-description" />
        <div className="flex items-center gap-3">
          <select className="input-base w-40" value={form.file_type} onChange={(e) => setForm({ ...form, file_type: e.target.value })} data-testid="form-type">
            <option value="pdf">PDF</option><option value="docx">DOCX</option><option value="xlsx">XLSX</option><option value="other">Other</option>
          </select>
          <button className="btn-primary" disabled={busy} data-testid="form-submit">{busy ? "Adding…" : "Add form"}</button>
        </div>
      </form>
    </div>
  );
}
